// Substitute aircraft group table – interchangeable equipment codes.
// Keep this in sync with the same list on the Bids & Schedule page.
export const AIRCRAFT_GROUPS = {
  regional: ["CR", "DH4", "E75", "DH8", "CRJ", "CR9"],
  narrow:   ["319", "320", "321", "223", "7M8", "738"],
  wide:     ["77L", "77W", "333", "788", "789", "77F", "76F"],
};

/** Return the group that this aircraft type belongs to, or null.
 *  Matches loosely — strips leading airframe letters (B/A/CR/DH) as VAC's data
 *  is inconsistent (schedule stores "789", assignments store "B789"). */
export function aircraftGroup(type) {
  if (!type) return null;
  const raw = String(type).toUpperCase().trim();
  const stripped = raw.replace(/^(B|A)(?=\d)/, "").replace(/^BOEING\s*/, "").replace(/^AIRBUS\s*/, "");
  for (const [k, arr] of Object.entries(AIRCRAFT_GROUPS)) {
    if (arr.includes(raw) || arr.includes(stripped)) return k;
  }
  return null;
}

/** Return all acceptable substitutes for a scheduled aircraft (includes itself). */
export function substitutesFor(type) {
  const grp = aircraftGroup(type);
  if (!grp) return type ? [type.toUpperCase()] : [];
  return AIRCRAFT_GROUPS[grp];
}

/**
 * Which registration the Init page should have selected.
 *
 * The assignment already names one. myvac's `choosefin()` (bids.func.php)
 * picks it at bid time from `paperwork/aircraft.txt`, honouring
 * operator-exclusive fins and the per-route restrictions in
 * `type_restrict.txt`, and where FlightAware knows the real-world flight it
 * uses the registration that actually operated it. VACARS5 used to discard
 * that and select `regs[0]` — the lowest fleet number for the type, which can
 * be a tail the route restriction excludes.
 *
 * Order: what the pilot explicitly picked, then the assigned tail, then the
 * first available. Each candidate must be in the catalogue for the type, so a
 * retired or wrong-type tail falls through rather than being submitted.
 *
 * Pure and derived on purpose — the previous effect-based version blanked the
 * selection whenever the fleet query was in flight, which is exactly when the
 * assigned tail had not arrived yet to defend itself.
 *
 * @param {Array<{reg: string}>} regs   fleet catalogue for the selected type
 * @param {string} assigned             bid.reg, may be empty
 * @param {string} chosen               the pilot's explicit pick, may be empty
 */
export function pickRegistration(regs, assigned, chosen) {
  const has = (r) => !!r && (regs || []).some((x) => x.reg === r);
  const up = (r) => String(r || "").toUpperCase();
  if (has(up(chosen))) return up(chosen);
  if (has(up(assigned))) return up(assigned);
  return (regs || [])[0]?.reg || "";
}

/** True when the aircraft the pilot was assigned is not one they can file. */
export function registrationSubstituted(regs, assigned) {
  if (!assigned) return false;
  if (!regs || !regs.length) return false;   // still loading — not yet an answer
  return !regs.some((x) => x.reg === String(assigned).toUpperCase());
}
